import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAccountAndSessionsCountByTypeQuery } from "@/api/hooks/util-hooks";
import AppFullPageTabs from "@/components/app-full-page-tabs";
import { ACC_TP } from "@/components/config-modals/router-edit-modal/constants";
import { COMMON_REFETCH_INTERVAL } from "@/lib/constants";
import DebugSessionsTable from "@/module/accounts-module/debug-sessions-table/debug-sessions-table";
import GameSessionsTable from "@/module/accounts-module/game-sessions-table/game-sessions-table";
import RegularAccountsTable from "@/module/accounts-module/regular-accounts-table/regular-accounts-table";
import ServiceAccountsTable from "@/module/accounts-module/service-accounts-table/service-accounts-table";
import ServiceSessionsTable from "@/module/accounts-module/service-sessions-table/service-sessions-table";
import { Route } from "@/routes/accounts/$room.$type";
import { AccountSessionType } from "@/types/accountTypes";
import { KeyFilter } from "@/shared/api";
import { useSysAccountsQuery } from "@/api/hooks/sys-account-hooks";
import { useSessionsQuery } from "@/api/hooks/sys-accont-sessions-hooks";
import AppFullPageTabsWithGroups from "@/components/app-full-page-tabs-with-groups";

export enum AccountType {
  regular = "regular",
  service = "service",
  gameSessions = "game-sessions",
  debugSessions = "debug-sessions",
  serviceSessions = "service-sessions",
}

const AccountsModule = () => {
  const { t } = useTranslation();

  const { room, type: unsafeAccountType } = Route.useParams();
  const navigate = Route.useNavigate();

  const accountType = useMemo<AccountType>(() => {
    return Object.values(AccountType).includes(unsafeAccountType as AccountType)
      ? (unsafeAccountType as AccountType)
      : AccountType.regular;
  }, [unsafeAccountType]);

  const setTabValue = (value: AccountType) => {
    void navigate({
      to: "/accounts/$room/$type",
      params: { room: room, type: value },
    });
  };


  const [regularAccountsAmount, setRegularAccountsAmount] = useState<number>(0);
  const [serviceAccountsAmount, setServiceAccountsAmount] = useState<number>(0);
  const [gameSessionsAmount, setGameSessionsAmount] = useState<number>(0);
  const [debugSessionsAmount, setDebugSessionsAmount] = useState<number>(0);
  const [serviceSessionsAmount, setServiceSessionsAmount] = useState<number>(0);



  const { data: sysRegularAccountsResponse} = useSysAccountsQuery({
      domain: room,
      filters: new Set<KeyFilter>([  { key: "data.account_type", mode: "STRICT_IN", value: [ACC_TP.REGULAR, ACC_TP.UNREGISTERED] as unknown as object },]),
      pagination: { pageIndex: 0, pageSize: 1, },
    },
  );

  const { data: sysServiceAccountsResponse} = useSysAccountsQuery({
      domain: room,
      filters: new Set<KeyFilter>([{ key: "data.account_type", mode: "STRICT_IN", value: [ACC_TP.GRABBER, ACC_TP.SCANNER, ACC_TP.MANAGER] as unknown as object },]),
      pagination: { pageIndex: 0, pageSize: 1, },
    },
  );

  const { data: sysGameSessionsResponse} = useSessionsQuery({
      domain: room,
      filters: new Set<KeyFilter>([
        {
          key: "$.session_type",
          mode: "EQUALS",
          value: AccountSessionType.GAME as unknown as object,
        },
      ]),
      pagination: { pageIndex: 0, pageSize: 1, },
    },
  );

  const { data: sysServiceSessionsResponse} = useSessionsQuery({
      domain: room,
      filters: new Set<KeyFilter>( [
        {
          key: "$.session_type",
          mode: "STRICT_IN",
          value: [
            AccountSessionType.GRABBER,
            AccountSessionType.MANAGER,
            AccountSessionType.SCANNER,
          ] as unknown as object,
        },
      ]),
      pagination: { pageIndex: 0, pageSize: 1, },
    },
  );

  const { data: sysDebugSessionsResponse} = useSessionsQuery({
      domain: room,
      filters: new Set<KeyFilter>([
        {
          key: "$.session_type",
          mode: "EQUALS",
          value: AccountSessionType.DEBUG as unknown as object,
        },
      ]),
      pagination: { pageIndex: 0, pageSize: 1, },
    },
  );

  useEffect(() => {
    setRegularAccountsAmount(sysRegularAccountsResponse?.data.totalElements || 0);
    setServiceAccountsAmount(sysServiceAccountsResponse?.data.totalElements || 0);
    setGameSessionsAmount(sysGameSessionsResponse?.data.totalElements || 0);
    setServiceSessionsAmount(sysServiceSessionsResponse?.data.totalElements || 0);
    setDebugSessionsAmount(sysDebugSessionsResponse?.data.totalElements || 0);
  }, [sysRegularAccountsResponse, sysServiceAccountsResponse, sysGameSessionsResponse, sysServiceSessionsResponse, sysDebugSessionsResponse]);


  return (
    <AppFullPageTabsWithGroups
      onTabChange={(val) => setTabValue(val)}
      defaultValue={accountType}
      groups={[
        {
          label: t("accounts"),
          items:
            [{
              value: AccountType.regular,
              label: t("accountsAmount", { amount: regularAccountsAmount }),
              render: () => <RegularAccountsTable setTotalAccountsNumber={(number: number) => setRegularAccountsAmount(number)} roomName={room} isOpen />,
            },
            {
              value: AccountType.service,
              label: t("serviceAccountsAmount", { amount: serviceAccountsAmount }),
              render: () => <ServiceAccountsTable setTotalAccountsNumber={(number: number) => setServiceAccountsAmount(number)} roomName={room} isOpen />,
            }]
        },
        {
          label: t("sessions"),
          items: [
            {
              value: AccountType.gameSessions,
              label: t("gameSessAmount", { amount: gameSessionsAmount }),
              render: () => <GameSessionsTable setTotalAccountsNumber={(number: number) => setGameSessionsAmount(number)} roomName={room} isOpen />,
            },
            {
              value: AccountType.serviceSessions,
              label: t("serviceSessAmount", { amount: serviceSessionsAmount }),
              render: () => <ServiceSessionsTable setTotalAccountsNumber={(number: number) => setServiceSessionsAmount(number)} roomName={room} isOpen />,
            },
            {
              value: AccountType.debugSessions,
              label: t("debugSessAmount", { amount: debugSessionsAmount }),
              render: () => <DebugSessionsTable setTotalAccountsNumber={(number: number) => setDebugSessionsAmount(number)} roomName={room} isOpen />,
            },
          ]
        }
      ]}
    />
  );


};

export default AccountsModule;
